import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Shield, CheckCircle, AlertCircle, ExternalLink, 
  ArrowUpRight, Loader2, RefreshCw, Zap, Rocket, TrendingUp, Star, Building2, Clock
} from 'lucide-react';
import { getPlanColor, getLiveEligibilityBadge, fmtCr } from './billingHelpers';
import { useBillingCurrency } from '../../hooks/useBillingCurrency';
import { RazorpayBillingTile } from './RazorpayBillingTile';

const getPlanIconEl = (planId) => {
  switch (planId) {
    case 'free': return <Zap className="h-6 w-6" />;
    case 'starter': return <Rocket className="h-6 w-6" />;
    case 'growth': return <TrendingUp className="h-6 w-6" />;
    case 'scale': return <Star className="h-6 w-6" />;
    case 'enterprise': return <Building2 className="h-6 w-6" />;
    default: return <Star className="h-6 w-6" />;
  }
};

export const BillingOverviewTab = ({
  currentPlan, billing, liveEligibility, user,
  renewalStatus, portalLoading, onUpgrade, onOpenPortal
}) => {
  const bc = useBillingCurrency();
  const showRenewalPill = renewalStatus && ['processing', 'action_required', 'pending'].includes(renewalStatus.status);
  const isAction = renewalStatus?.status === 'action_required';
  return (
    <div className="space-y-6">
      {/* Renewal payment in-flight — explains why credits haven't landed yet */}
      {showRenewalPill && (
        <Card
          data-testid="renewal-payment-status-card"
          className={isAction ? 'border-amber-500/40 bg-amber-500/5' : 'border-blue-500/40 bg-blue-500/5'}
        >
          <CardContent className="py-4 px-6">
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${isAction ? 'bg-amber-500/15' : 'bg-blue-500/15'}`}>
                {isAction ? <AlertCircle className="h-5 w-5 text-amber-400" /> : <Clock className="h-5 w-5 text-blue-400" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`font-semibold ${isAction ? 'text-amber-400' : 'text-blue-400'}`}>
                    {isAction ? 'Renewal payment needs confirmation' : 'Credits arrive when your bank confirms'}
                  </p>
                  <Badge variant="secondary" className="text-xs" data-testid="renewal-payment-status-badge">
                    {isAction ? 'Action required' : 'Processing'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1" data-testid="renewal-payment-status-message">
                  {renewalStatus.message}
                  {renewalStatus.invoice_number ? ` (Invoice ${renewalStatus.invoice_number}` : ''}
                  {renewalStatus.invoice_number && renewalStatus.amount_usd ? `, $${renewalStatus.amount_usd.toFixed(2)})` : (renewalStatus.invoice_number ? ')' : '')}
                </p>
                {renewalStatus.hosted_invoice_url && (
                  <Button
                    variant={isAction ? 'default' : 'outline'}
                    size="sm"
                    className="mt-3"
                    asChild
                    data-testid="renewal-payment-view-btn"
                  >
                    <a href={renewalStatus.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {isAction ? 'Complete payment' : 'View invoice'}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Current Plan Card */}
      <Card className="border-primary/30" data-testid="current-plan-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-primary/10 ${getPlanColor(currentPlan?.plan_id)}`}>
                {getPlanIconEl(currentPlan?.plan_id)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle>{currentPlan?.name || 'Free'} Plan</CardTitle>
                  {getLiveEligibilityBadge(currentPlan, Badge)}
                </div>
                <CardDescription>{currentPlan?.description}</CardDescription>
              </div>
            </div>
            <div className="text-right">
              {currentPlan?.monthly_price_usd !== null && currentPlan?.monthly_price_usd !== undefined ? (
                <div className="text-3xl font-bold">
                  {bc.currency === 'INR' && currentPlan.monthly_price_inr != null
                    ? `₹${Number(currentPlan.monthly_price_inr).toLocaleString('en-IN')}`
                    : `$${currentPlan.monthly_price_usd}`}
                  <span className="text-sm font-normal text-muted-foreground">/mo{bc.currency === 'INR' ? ' + GST' : ''}</span>
                </div>
              ) : (
                <div className="text-lg font-semibold text-amber-400">Contact Sales</div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground">{currentPlan?.plan_id === 'free' ? 'Signup Bonus' : 'Included Credits'}</div>
              <div className="text-xl font-semibold">{(currentPlan?.included_credits ?? fmtCr(currentPlan?.included_credits_usd || 0)).toLocaleString()} Cr</div>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground">Max AI Agents</div>
              <div className="text-xl font-semibold">{currentPlan?.max_agents || 2}</div>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground">LIVE Mode</div>
              <div className="text-xl font-semibold">
                {currentPlan?.live_mode_allowed ? (
                  <span className="text-green-400">Allowed</span>
                ) : (
                  <span className="text-slate-400">Not Available</span>
                )}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground">Payment Status</div>
              <div className="text-xl font-semibold">
                {billing?.billing_status === 'active' ? (
                  <span className="text-green-400">Active</span>
                ) : billing?.billing_status === 'past_due' ? (
                  <span className="text-red-400">Past Due</span>
                ) : (
                  <span className="text-slate-400">{billing?.billing_status || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button onClick={onUpgrade} data-testid="upgrade-plan-btn">
            <ArrowUpRight className="h-4 w-4 mr-2" />
            Upgrade Plan
          </Button>
          {billing?.stripe_customer_id && (user?.role === 'tenant_admin' || user?.role === 'org_admin') && (
            <Button variant="outline" onClick={onOpenPortal} disabled={portalLoading} data-testid="manage-billing-btn">
              {portalLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening...</>
              ) : (
                <><ExternalLink className="h-4 w-4 mr-2" />Manage Billing</>
              )}
            </Button>
          )}
        </CardFooter>
      </Card>

      <RazorpayBillingTile />
      {/* LIVE Mode Status */}
      <div className="grid grid-cols-1 gap-6">
        <Card data-testid="live-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-400" />
              LIVE Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">
              {liveEligibility?.eligible ? (
                <span className="text-green-400 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />Eligible
                </span>
              ) : (
                <span className="text-amber-400 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />Not Eligible
                </span>
              )}
            </div>
            {liveEligibility?.reasons?.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">{liveEligibility.reasons[0]}</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">* Subject to Phase B governance checks</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
